// src/modules/chat/ChatContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Global chat state — real Ollama streaming, conversation persistence,
// unread counts, window management
// ─────────────────────────────────────────────────────────────────────────────

import {
  createContext, useContext, useState, useCallback,
  useRef, useEffect,
} from 'react';
import {
  getEntity,
  ENTITY_TYPES,
  streamOllamaChat,
  buildOllamaMessages,
  checkOllamaHealth,
  getWelcomeMessage,
  getOfflineResponse,
} from './chatStore';
import { trackAction } from '../../utils/actionStore';

const ChatContext = createContext(null);

const MAX_OPEN_WINDOWS = 3;
const CONVO_STORAGE_KEY = (uid) => `dd_chat_convos_${uid || 'anon'}`;
const getActiveModule = () => {
  if (typeof window === 'undefined') return 'home';
  return window.__dd_active_module || 'home';
};
const getUserKey = () => {
  try {
    const token = localStorage.getItem('dd_token');
    if (!token) return 'anon';
    // Extract user id from JWT payload (base64 decode middle segment)
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub || payload.id || 'anon';
  } catch (_) { return 'anon'; }
};

// ── Load / save conversations ─────────────────────────────────────────────────
function loadConvos(uid) {
  try {
    const raw = localStorage.getItem(CONVO_STORAGE_KEY(uid));
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
}

function saveConvos(uid, convos) {
  try {
    // Only persist non-streaming messages, cap each convo at 100 messages
    const toSave = Object.fromEntries(
      Object.entries(convos).map(([id, msgs]) => [
        id,
        (msgs || []).filter(m => !m.streaming && !m.typing).slice(-100),
      ])
    );
    localStorage.setItem(CONVO_STORAGE_KEY(uid), JSON.stringify(toSave));
  } catch (_) {}
}

export function ChatProvider({ children }) {
  const uid = getUserKey();

  // ── State ─────────────────────────────────────────────────────────────────
  const [windows,    setWindows]    = useState([]);
  const [convos,     setConvos]     = useState(() => loadConvos(uid));
  const [unread,     setUnread]     = useState({});
  const [searchOpen, setSearchOpen] = useState(false);

  // Ollama health — checked once on mount
  const [ollamaOk,     setOllamaOk]     = useState(null);
  const [ollamaModels, setOllamaModels] = useState([]);

  const abortRefs = useRef({});  // entityId → AbortController

  // ── Check Ollama health on mount ─────────────────────────────────────────
  useEffect(() => {
    checkOllamaHealth().then(({ ok, models }) => {
      setOllamaOk(ok);
      setOllamaModels(models);
    });
    // Re-check when tab regains focus (user may have started ollama)
    const onFocus = () => checkOllamaHealth().then(({ ok, models }) => {
      setOllamaOk(ok);
      setOllamaModels(models);
    });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // ── Persist conversations ────────────────────────────────────────────────
  useEffect(() => {
    saveConvos(uid, convos);
  }, [convos, uid]);

  // ── Listen for agent config changes from LocalAgents ─────────────────────
  // When a user configures an agent, re-check Ollama to see new models
  useEffect(() => {
    const handler = () => {
      checkOllamaHealth().then(({ ok, models }) => {
        setOllamaOk(ok);
        setOllamaModels(models);
      });
    };
    window.addEventListener('dd:agent:configured', handler);
    return () => window.removeEventListener('dd:agent:configured', handler);
  }, []);

  // ── Open a chat window ───────────────────────────────────────────────────
  const openChat = useCallback((entityId) => {
    const entity = getEntity(entityId);
    setWindows(prev => {
      const existing = prev.find(w => w.entityId === entityId);
      if (existing) {
        return prev.map(w =>
          w.entityId === entityId ? { ...w, minimized: false } : w
        );
      }
      const newWindow = { id: Date.now(), entityId, minimized: false };
      const updated = [newWindow, ...prev];
      if (updated.length > MAX_OPEN_WINDOWS) {
        return updated.map((w, i) =>
          i >= MAX_OPEN_WINDOWS ? { ...w, minimized: true } : w
        );
      }
      return updated;
    });

    // Inject welcome message if conversation is empty
    setConvos(prev => {
      if (!prev[entityId] || prev[entityId].length === 0) {
        const welcome = getWelcomeMessage(entityId);
        if (welcome) return { ...prev, [entityId]: [welcome] };
      }
      return prev;
    });

    setUnread(prev => ({ ...prev, [entityId]: 0 }));

    if (entity) {
      trackAction({
        module: getActiveModule(),
        type: 'chat:open',
        label: `Open chat: ${entity.name}`,
        group: 'chat',
        icon: '💬',
        params: { entityId },
        replayable: false,
        meta: { handle: entity.handle },
      });
    }
  }, []);

  const closeChat = useCallback((entityId) => {
    // Abort any in-progress stream
    abortRefs.current[entityId]?.abort();
    delete abortRefs.current[entityId];
    setWindows(prev => prev.filter(w => w.entityId !== entityId));
  }, []);

  const toggleMinimize = useCallback((entityId) => {
    setWindows(prev =>
      prev.map(w =>
        w.entityId === entityId ? { ...w, minimized: !w.minimized } : w
      )
    );
  }, []);

  // ── Send message + stream Ollama response ────────────────────────────────
  const sendMessage = useCallback(async (entityId, text) => {
    if (!text?.trim()) return;

    const entity = getEntity(entityId);
    if (!entity) return;

    trackAction({
      module: getActiveModule(),
      type: 'chat:message',
      label: `Message to ${entity.name}`,
      group: 'chat',
      icon: '✉️',
      params: { entityId, length: text.trim().length },
      replayable: false,
      meta: { handle: entity.handle },
    });

    // 1. Add user message immediately
    const userMsg = {
      id:     `msg_${Date.now()}`,
      from:   'me',
      text:   text.trim(),
      ts:     Date.now(),
      status: 'sent',
    };

    setConvos(prev => ({
      ...prev,
      [entityId]: [...(prev[entityId] || []), userMsg],
    }));

    // 2. If agent has no brain or is "coming soon", return config nudge
    if (entity.comingSoon || !entity.brainId) {
      const nudge = {
        id:   `msg_${Date.now() + 1}`,
        from: entityId,
        text: `I need a brain to answer properly! Go to **Local AI Agents** → find me → click **⚙ Configure** → assign a model under the 🧠 Brain tab. Once configured, I'll be live here.`,
        ts:   Date.now() + 500,
        status: 'delivered',
        isNudge: true,
      };
      setTimeout(() => {
        setConvos(prev => ({
          ...prev,
          [entityId]: [...(prev[entityId] || []), nudge],
        }));
        setUnread(prev => ({ ...prev, [entityId]: (prev[entityId] || 0) + 1 }));
      }, 600);
      return;
    }

    // 3. Check if model is actually loaded in Ollama
    const modelAvailable = ollamaModels.includes(entity.brainId);

    if (!ollamaOk || !modelAvailable) {
      setTimeout(() => {
        const offlineMsg = {
          id:     `msg_${Date.now() + 1}`,
          from:   entityId,
          text:   getOfflineResponse(entityId),
          ts:     Date.now(),
          status: 'delivered',
          isOffline: true,
        };
        setConvos(prev => ({
          ...prev,
          [entityId]: [...(prev[entityId] || []), offlineMsg],
        }));
        setUnread(prev => ({ ...prev, [entityId]: (prev[entityId] || 0) + 1 }));
      }, 400);
      return;
    }

    // 4. Show typing indicator
    const typingId = `typing_${Date.now()}`;
    setConvos(prev => ({
      ...prev,
      [entityId]: [
        ...(prev[entityId] || []),
        { id: typingId, from: entityId, typing: true, ts: Date.now() },
      ],
    }));

    // 5. Build messages and start streaming
    const currentHistory = convos[entityId] || [];
    const ollamaMessages = buildOllamaMessages(entity, currentHistory, text.trim());

    const abort = new AbortController();
    abortRefs.current[entityId] = abort;

    // Replace typing indicator with a streaming message placeholder
    const streamId = `stream_${Date.now()}`;
    setConvos(prev => ({
      ...prev,
      [entityId]: [
        ...(prev[entityId] || []).filter(m => m.id !== typingId),
        { id: streamId, from: entityId, text: '', ts: Date.now(), streaming: true },
      ],
    }));

    let fullText = '';
    try {
      for await (const token of streamOllamaChat({
        model:       entity.brainId,
        messages:    ollamaMessages,
        temperature: entity.temperature ?? 0.4,
        signal:      abort.signal,
      })) {
        fullText += token;
        // Update streaming message in place
        setConvos(prev => ({
          ...prev,
          [entityId]: (prev[entityId] || []).map(m =>
            m.id === streamId ? { ...m, text: fullText } : m
          ),
        }));
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        // User aborted — keep whatever was streamed
      } else {
        fullText = fullText || `Error: ${e.message}. Check that Ollama is running with \`ollama serve\`.`;
      }
    }

    // 6. Finalise — replace streaming placeholder with permanent message
    setConvos(prev => ({
      ...prev,
      [entityId]: (prev[entityId] || []).map(m =>
        m.id === streamId
          ? { ...m, text: fullText || '…', streaming: false, status: 'delivered' }
          : m
      ),
    }));

    // Update read receipt on sent message
    setConvos(prev => ({
      ...prev,
      [entityId]: (prev[entityId] || []).map(m =>
        m.id === userMsg.id ? { ...m, status: 'read' } : m
      ),
    }));

    // Increment unread if window is minimized
    setWindows(prev => {
      const win = prev.find(w => w.entityId === entityId);
      if (win?.minimized) {
        setUnread(u => ({ ...u, [entityId]: (u[entityId] || 0) + 1 }));
      }
      return prev;
    });

    delete abortRefs.current[entityId];
  }, [convos, ollamaOk, ollamaModels]);

  // ── Abort active stream ──────────────────────────────────────────────────
  const abortStream = useCallback((entityId) => {
    abortRefs.current[entityId]?.abort();
    delete abortRefs.current[entityId];
    // Remove streaming placeholder
    setConvos(prev => ({
      ...prev,
      [entityId]: (prev[entityId] || []).map(m =>
        m.streaming ? { ...m, streaming: false, text: m.text + ' [stopped]' } : m
      ),
    }));
  }, []);

  // ── Start a fresh conversation ────────────────────────────────────────────
  const startNewChat = useCallback((entityId) => {
    abortRefs.current[entityId]?.abort();
    const welcome = getWelcomeMessage(entityId);
    setConvos(prev => ({
      ...prev,
      [entityId]: welcome ? [welcome] : [],
    }));
    openChat(entityId);
    setSearchOpen(false);
  }, [openChat]);

  const totalUnread = Object.values(unread).reduce((a, b) => a + b, 0);

  // ── Check if a specific entity has an active stream ──────────────────────
  const isStreaming = useCallback((entityId) => {
    return !!(abortRefs.current[entityId]);
  }, []);

  return (
    <ChatContext.Provider value={{
      windows, convos, unread, totalUnread,
      searchOpen, setSearchOpen,
      ollamaOk, ollamaModels,
      openChat, closeChat, toggleMinimize,
      sendMessage, startNewChat, abortStream, isStreaming,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be inside <ChatProvider>');
  return ctx;
};
